import React from "react";
import { useTranslation } from "@me8eon/translation";
import { SelectedCategory, fmt } from "../poData";
import TabSkeleton from "../components/TabSkeleton";
import TabError from "../components/TabError";
import { DashboardRange } from "../../../services/dashboardApi";
import { useDashboardData } from "../useDashboardData";

type Props = {
    kind: "callback" | "liveChat";
    range: DashboardRange;
    periodDays: number;
    isCustomPeriod: boolean;
    categoryFilter: SelectedCategory[];
    companyFilter: string[];
};

/** Full-screen language breakdown for a contact-request card (callback / live chat) —
 *  every language uncollapsed, not just the card's En/De/Others buckets. Same layout as
 *  Total Queries' category breakdown table. */
const RequestBreakdownTab: React.FC<Props> = ({ kind, range, periodDays, isCustomPeriod, categoryFilter, companyFilter }) => {
    const t = useTranslation();
    const tp = (key: string) => t(`productDashboard.requestBreakdown.${key}`);
    const tc = (key: string) => t(`productDashboard.common.${key}`);
    const { callbackRequests, liveChatRequests, loading, error } = useDashboardData(range, categoryFilter, companyFilter);
    const data = kind === "callback" ? callbackRequests : liveChatRequests;
    const maxCount = Math.max(1, ...data.languages.map((l) => l.count));

    if (loading) {
        return (
            <div className="po-queries">
                <TabSkeleton />
            </div>
        );
    }

    if (error) {
        return (
            <div className="po-queries">
                <TabError />
            </div>
        );
    }

    return (
        <div className="po-queries">
            <div className="po-section-head">
                <div>
                    <h2>{tp(kind === "callback" ? "titleCallback" : "titleLiveChat")}</h2>
                    <p className="po-section-head__sub">
                        {fmt(data.total)} {data.total === 1 ? tp("request") : tp("requests")} · {tc(isCustomPeriod ? "for" : "last")} {periodDays} {tc(periodDays === 1 ? "day" : "days")}
                    </p>
                </div>
            </div>

            <div className="po-card po-table-card">
                <div className="po-card__head">
                    <h3>{tp("breakdownTitle")}</h3>
                    <span className="po-card__meta">{data.languages.length} {tp(data.languages.length === 1 ? "language" : "languages")}</span>
                </div>
                <table className="po-table">
                    <thead>
                        <tr>
                            <th>{tp("thLanguage")}</th>
                            <th className="num">{tp("thCount")}</th>
                            <th className="num">{tp("thShare")}</th>
                            <th>{tp("thDistribution")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.languages.map((l) => (
                            <tr key={l.key}>
                                <td className="po-table__strong">{l.label}</td>
                                <td className="num">{fmt(l.count)}</td>
                                <td className="num po-table__muted">{data.total ? Math.round((l.count / data.total) * 1000) / 10 : 0}%</td>
                                <td>
                                    <div className="po-minibar">
                                        <div className="po-minibar__fill" style={{ width: `${(l.count / maxCount) * 100}%` }} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {data.languages.length === 0 && (
                            <tr>
                                <td colSpan={4} className="po-table__muted">
                                    {tp("empty")}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default RequestBreakdownTab;
